import { type FC } from "react";

import { useDeepRootSelector } from "@ds/hooks";

import { MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { AggregatedValue } from "@ds/components/entities";
import { IconType } from "@ds/components/icons";
import { InfoAttribute, InfoSection, InfoView, InfoViewCallToAction } from "@ds/components/info-view";
import { getInfoViewTitle } from "@ds/components/info-view/helpers";
import { formatBytes, formatChannels, formatDuration, formatSampleRate } from "@ds/utils/localization/assets";

import { useDropzoneRef } from "../hooks/use-dropzone-ref";
import { selectAggregatedAssetForSelectedEntities } from "../redux/selectors/common-selectors";
import { getContentTypeNameByTableType, getSupportedFormatsByTableType } from "../utils/helpers";
import { AssetUploadInput } from "./forms/common/assets-upload-input";

export const AssetInfo: FC<{
  tableType: MainTableDataTypeEnum;
  onUpload: (files: File[]) => void;
}> = ({ tableType, onUpload }) => {
  const [aggregatedAsset, assetPrintName] = useDeepRootSelector(state => [
    selectAggregatedAssetForSelectedEntities(state, tableType),
    getContentTypeNameByTableType(tableType),
  ]);

  const [dropzoneRef, openDropzone] = useDropzoneRef();

  if (aggregatedAsset) {
    return (
      <InfoView infoTitle={getInfoViewTitle(aggregatedAsset)}>
        <InfoSection title="Asset Info" icon="pi pi-info-circle">
          <InfoAttribute
            name="File Size"
            value={
              <AggregatedValue
                entity={aggregatedAsset}
                field="file_size"
                formatter={entity => formatBytes(entity.file_size)}
              />
            }
          />

          <InfoAttribute
            name="Content Type"
            value={<AggregatedValue entity={aggregatedAsset} field="content_type" />}
          />
        </InfoSection>

        {(aggregatedAsset.media_info?.width ||
          aggregatedAsset.media_info?.height ||
          (aggregatedAsset.media_info?.duration && !aggregatedAsset.content_type?.startsWith("audio/"))) && (
          <InfoSection
            title={aggregatedAsset.content_type?.startsWith("image/") ? "Image Details" : "Video Details"}
            icon={aggregatedAsset.content_type?.startsWith("image/") ? "pi pi-image" : "pi pi-video"}
          >
            {aggregatedAsset.media_info.duration && (
              <InfoAttribute name="Duration" value={formatDuration(aggregatedAsset.media_info.duration)} />
            )}

            {aggregatedAsset.media_info.width && aggregatedAsset.media_info.height && (
              <InfoAttribute
                name="Dimensions"
                value={`${aggregatedAsset.media_info.width} × ${aggregatedAsset.media_info.height}`}
              />
            )}
          </InfoSection>
        )}

        {(aggregatedAsset.media_info?.sample_rate ||
          aggregatedAsset.media_info?.channels ||
          (aggregatedAsset.media_info?.duration && aggregatedAsset.content_type?.startsWith("audio/"))) && (
          <InfoSection title="Audio Details" icon="pi pi-volume-up">
            {aggregatedAsset.media_info.duration && aggregatedAsset.content_type?.startsWith("audio/") && (
              <InfoAttribute name="Duration" value={formatDuration(aggregatedAsset.media_info.duration)} />
            )}

            {aggregatedAsset.media_info.sample_rate && (
              <InfoAttribute name="Sample Rate" value={formatSampleRate(aggregatedAsset.media_info.sample_rate)} />
            )}

            {aggregatedAsset.media_info.channels && (
              <InfoAttribute name="Channels" value={formatChannels(aggregatedAsset.media_info.channels)} />
            )}
          </InfoSection>
        )}
      </InfoView>
    );
  }

  return (
    <>
      <InfoViewCallToAction
        infoTitle={`Select ${assetPrintName}`}
        callToActionText={`Select a ${assetPrintName} asset in the table for more info or proceed with uploading new ${assetPrintName}.`}
        buttonLabel={`Upload ${assetPrintName}`}
        buttonIcon={IconType.Upload}
        onActionButtonClick={openDropzone}
      />

      <AssetUploadInput
        acceptMediaTypes={getSupportedFormatsByTableType(tableType)}
        dropzoneRef={dropzoneRef}
        onUpload={onUpload}
      />
    </>
  );
};
